const RISK_LIMITS = {
  rain: 5,
  heat: 34,
  wind: 4,
  lowHumidity: 35,
};

function localDateKey(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function currentForecastDateKey(referenceDate = new Date()) {
  return localDateKey(referenceDate);
}

export function forecastDateKey(value) {
  const match = String(value || '').match(/^(\d{4}-\d{2}-\d{2})/);
  return match?.[1] || null;
}

export function formatForecastDate(value, options = { day: '2-digit', month: '2-digit' }) {
  const key = forecastDateKey(value);
  if (!key) return String(value || '');
  return new Date(`${key}T12:00:00`).toLocaleDateString('pt-BR', options);
}

export function normalizeCurrentForecast(rows, referenceDate = new Date()) {
  const todayKey = currentForecastDateKey(referenceDate);
  return (rows || [])
    .filter(row => {
      const key = forecastDateKey(row?.date);
      return key && key >= todayKey;
    })
    .sort((a, b) => forecastDateKey(a.date).localeCompare(forecastDateKey(b.date)));
}

export function forecastDayLabel(dateValue, referenceDate = new Date()) {
  const key = forecastDateKey(dateValue);
  if (!key) return 'Data';

  const todayKey = currentForecastDateKey(referenceDate);
  const tomorrow = new Date(referenceDate);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowKey = localDateKey(tomorrow);

  if (key === todayKey) return 'Hoje';
  if (key === tomorrowKey) return 'Amanhã';

  return formatForecastDate(key, { weekday: 'short' }).replace('.', '');
}

export function forecastFullDayLabel(dateValue, referenceDate = new Date()) {
  const shortLabel = forecastDayLabel(dateValue, referenceDate);
  if (shortLabel === 'Hoje' || shortLabel === 'Amanhã' || shortLabel === 'Data') return shortLabel;
  return formatForecastDate(dateValue, { weekday: 'long' });
}

export function evaluateForecastRisks(day) {
  const rain = Number(day?.ppt || 0);
  const maxTemperature = Number(day?.tmax || 0);
  const wind = Number(day?.ws2m || 0);
  const humidity = Number(day?.ur ?? 100);
  const risks = [];

  if (rain >= RISK_LIMITS.rain) risks.push({ type: 'rain', label: `chuva de ${rain.toFixed(1)} mm` });
  if (maxTemperature >= RISK_LIMITS.heat) risks.push({ type: 'heat', label: `temperatura de ${maxTemperature.toFixed(0)}°C` });
  if (wind >= RISK_LIMITS.wind) risks.push({ type: 'wind', label: `vento de ${wind.toFixed(1)} m/s` });
  if (humidity <= RISK_LIMITS.lowHumidity) risks.push({ type: 'humidity', label: `umidade de ${humidity.toFixed(0)}%` });

  return risks;
}

export function isFavorableOperationalDay(day) {
  return Number(day?.ppt || 0) < 1
    && Number(day?.ws2m || 0) < 3.5
    && Number(day?.tmax || 0) < 34
    && Number(day?.ur ?? 100) > 35;
}

export function buildForecastOperationalInsights(rows, referenceDate = new Date()) {
  const nextSeven = normalizeCurrentForecast(rows, referenceDate).slice(0, 7);
  if (!nextSeven.length) return null;

  const nextThree = nextSeven.slice(0, 3);
  const sum = (items, key) => items.reduce((total, item) => total + Number(item?.[key] || 0), 0);
  const max = (key) => Math.max(...nextSeven.map(item => Number(item?.[key] || 0)));
  const min = (key) => Math.min(...nextSeven.map(item => Number(item?.[key] ?? 100)));
  const riskDays = nextSeven.map((day, index) => ({ day, index, risks: evaluateForecastRisks(day) }));
  const firstRisk = riskDays.find(item => item.risks.length > 0) || null;

  return {
    nextSeven,
    rainThree: sum(nextThree, 'ppt'),
    rainSeven: sum(nextSeven, 'ppt'),
    etoSeven: sum(nextSeven, 'eto_mm_day'),
    maxTemperature: max('tmax'),
    maxWind: max('ws2m'),
    minHumidity: min('ur'),
    favorableDays: nextSeven.filter(isFavorableOperationalDay).length,
    firstRisk,
    primaryDay: nextSeven[0],
    primaryDayLabel: forecastDayLabel(nextSeven[0].date, referenceDate),
    favorablePrimaryDay: isFavorableOperationalDay(nextSeven[0]),
  };
}

export { RISK_LIMITS };
